import { Injectable } from '@angular/core';

/**
 * The logo stamped onto every exported Excel/PDF file. Change this one line to
 * rebrand every export in the system.
 */
export const EXPORT_LOGO_ASSET_PATH = 'assets/images/Logo.png';

/**
 * Rendered size of the logo in exported files, in pixels.
 * Keep this aspect ratio aligned with assets/images/Logo.png (~3.8:1) so
 * Excel/MHTML do not squash the mark.
 */
export const EXPORT_LOGO_SIZE = { width: 200, height: 53 };

/**
 * Height of the worksheet row reserved for the logo, in points (Excel's unit
 * for row height, 1pt = 1/72"). Sized to clear EXPORT_LOGO_SIZE.height plus a
 * little breathing room, so the image doesn't overlap the first data row.
 */
export const EXPORT_LOGO_ROW_HEIGHT = EXPORT_LOGO_SIZE.height * 0.75 + 6;

/** File name the logo MIME part is published under inside an MHTML export. */
const MHTML_LOGO_NAME = 'logo.png';

/**
 * Base the MHTML parts are addressed from. The value is arbitrary — it only has
 * to be a consistent absolute URL, because the <img src> resolves against it to
 * find the image part.
 */
const MHTML_BASE = 'file:///C:/export/';

const MHTML_BOUNDARY = '----=_NextPart_ExportLogo';

/**
 * Single source of the export logo.
 *
 * Exports must carry the image with them: a file opened from disk has no page
 * origin, so a relative or portal URL resolves to nothing and Excel draws a
 * broken-image box. There are exactly two ways to do that, and which one you
 * need depends on the file format:
 *
 *  - Real .xlsx — `getLogoBuffer()` for `ExcelJS.Workbook.addImage`.
 *  - HTML-table .xls — `buildExcelMhtml()`. Note that a `data:` URI does NOT
 *    work here: Excel's HTML importer never decodes them, it only follows links,
 *    so the logo has to travel as a separate MIME part alongside the HTML.
 */
@Injectable({
  providedIn: 'root',
})
export class ExportBrandingService {
  private logoDataUrl?: Promise<string>;
  private logoBuffer?: Promise<ArrayBuffer>;

  /** The logo as a `data:image/png;base64,...` URL. Fetched once per session. */
  getLogoDataUrl(): Promise<string> {
    if (!this.logoDataUrl) {
      this.logoDataUrl = this.fetchLogoBlob().then(blob =>
        this.blobToDataUrl(blob)
      );
    }
    return this.logoDataUrl;
  }

  /** The logo's raw bytes, for `ExcelJS.Workbook.addImage`. Fetched once per session. */
  getLogoBuffer(): Promise<ArrayBuffer> {
    if (!this.logoBuffer) {
      this.logoBuffer = this.fetchLogoBlob().then(blob => blob.arrayBuffer());
    }
    return this.logoBuffer;
  }

  /**
   * The logo `<img>` tag for an HTML document that will be shipped through
   * `buildExcelMhtml`. The src is a bare file name, resolved by Excel against the
   * HTML part's own location to reach the logo's MIME part — so this tag only
   * renders inside an MHTML document, never in a plain HTML file.
   *
   * Excel honours width/height attributes but ignores most CSS here, so the size
   * is set as attributes rather than a style rule.
   */
  getLogoImgTag(): string {
    return `<img src="${MHTML_LOGO_NAME}" width="${EXPORT_LOGO_SIZE.width}" height="${EXPORT_LOGO_SIZE.height}" alt="">`;
  }

  /**
   * Package an HTML document and the logo into a single MHTML (multipart/related)
   * blob that Excel opens as a workbook with the image rendered — the same
   * container Excel itself writes for "Save as Single File Web Page".
   *
   * Excel still does the table layout, so the caller's HTML (styles, colspan,
   * rowspan) behaves exactly as it did before the logo existed.
   *
   * If the logo can't be loaded, returns the plain HTML blob instead: an
   * unreachable logo costs the user a logo, not their export.
   */
  async buildExcelMhtml(htmlDocument: string): Promise<Blob> {
    const type = 'application/vnd.ms-excel';
    let logoBase64: string;
    try {
      logoBase64 = await this.getLogoBase64();
    } catch (error) {
      console.error('Could not embed export logo:', error);
      return new Blob([htmlDocument], { type });
    }

    // MIME requires CRLF line endings, and base64 bodies wrapped at 76 chars.
    const mhtml = [
      'MIME-Version: 1.0',
      `Content-Type: multipart/related; boundary="${MHTML_BOUNDARY}"; type="text/html"`,
      '',
      `--${MHTML_BOUNDARY}`,
      `Content-Location: ${MHTML_BASE}report.htm`,
      'Content-Type: text/html; charset="utf-8"',
      'Content-Transfer-Encoding: base64',
      '',
      this.wrapBase64(this.utf8ToBase64(htmlDocument)),
      '',
      `--${MHTML_BOUNDARY}`,
      `Content-Location: ${MHTML_BASE}${MHTML_LOGO_NAME}`,
      'Content-Type: image/png',
      'Content-Transfer-Encoding: base64',
      '',
      this.wrapBase64(logoBase64),
      '',
      `--${MHTML_BOUNDARY}--`,
      '',
    ].join('\r\n');

    return new Blob([mhtml], { type });
  }

  /** The logo's base64 payload, without the `data:` URI prefix. */
  private async getLogoBase64(): Promise<string> {
    const dataUrl = await this.getLogoDataUrl();
    return dataUrl.slice(dataUrl.indexOf(',') + 1);
  }

  private wrapBase64(base64: string): string {
    return (base64.match(/.{1,76}/g) ?? []).join('\r\n');
  }

  private utf8ToBase64(text: string): string {
    const bytes = new TextEncoder().encode(text);
    let binary = '';
    // Chunked to stay well under the argument limit of String.fromCharCode.
    for (let i = 0; i < bytes.length; i += 0x8000) {
      binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    }
    return btoa(binary);
  }

  private async fetchLogoBlob(): Promise<Blob> {
    const response = await fetch(EXPORT_LOGO_ASSET_PATH);
    if (!response.ok) {
      throw new Error(
        `Could not load export logo from ${EXPORT_LOGO_ASSET_PATH}: ${response.status}`
      );
    }
    return response.blob();
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  }
}
